/**
 * Google Analytics 4 view: property picker, date range, total tiles (each number links to its breakdown), by day,
 * by default channel group, by source / medium. Ga4Body is pure (any state from props; SSR tests render it);
 * Ga4View loads through a Ga4Source (customer or agency scope).
 * States: loading, feature off, error, no property (customer: link to Integrations; agency: a note), reconnect needed
 * (figures shown "as of" the last success, never as current), quota pause, days not yet synced, nothing in range, data.
 */
import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { formatCurrency, formatDateTime, formatNumber, sanitizeBidi, type Locale, type Translate } from '@qm/i18n';
import { Button, DataTable, Heading, type Column } from '@qm/ui';
import { BffError } from '../lib/bff';
import { ga4Empty, ga4Notices, ga4RangeFor, GA4_PRESETS, pickProperty, type Ga4Preset, type Ga4PropertyView, type Ga4Report, type Ga4Source, type Ga4SyncResult } from './logic';
import '../performance/performance.css';
import './ga4.css';

export type Async<T> = { status: 'loading' } | { status: 'error'; code?: string } | { status: 'ready'; data: T };
const codeOf = (e: unknown) => (e instanceof BffError ? e.envelope?.error.code ?? (e.status === 404 ? 'not_found' : undefined) : undefined);

type Row = Omit<Ga4Report['byChannel'][number], 'value'>;

interface Fmt { num: (n: number) => string; pct: (r: number) => string; money: (minor: number, currency: string) => string; when: (iso: string) => string; day: (iso: string) => string }
function fmtFor(locale: Locale): Fmt {
  return {
    num: (n) => formatNumber(locale, n, { maximumFractionDigits: 2 }),
    pct: (r) => formatNumber(locale, r, { style: 'percent', maximumFractionDigits: 1 }),
    money: (minor, currency) => formatCurrency(locale, minor, currency),
    when: (iso) => formatDateTime(locale, iso),
    day: (iso) => formatDateTime(locale, `${iso}T00:00:00Z`, { style: 'date', timeZone: 'UTC' }),
  };
}

function NotMeasured({ t }: { t: Translate }) {
  return <span className="qm-perf__nm">{t('connectors.ga4.notMeasured')}</span>;
}

export interface Ga4BodyProps {
  properties: Async<Ga4PropertyView[]>;
  report: Async<Ga4Report> | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
  preset: Ga4Preset;
  onPreset: (p: Ga4Preset) => void;
  t: Translate;
  locale: Locale;
  /** customer: where to connect / reconnect; null in the agency view (the client reconnects in their workspace) */
  integrationsHref: string | null;
  onRetry?: () => void;
  onSync?: () => void;
  syncing?: boolean;
  syncResult?: Ga4SyncResult | null;
}

export function Ga4Body(p: Ga4BodyProps) {
  const { t, locale } = p;
  const f = useMemo(() => fmtFor(locale), [locale]);
  const uid = useId();
  const ids = { tiles: `${uid}-tiles`, daily: `${uid}-daily`, channel: `${uid}-channel`, source: `${uid}-source`, prop: `${uid}-prop` };

  if (p.properties.status === 'loading') return <div className="qm-perf"><p className="qm-loading" role="status" aria-live="polite">{t('connectors.ga4.loading')}</p></div>;
  if (p.properties.status === 'error') {
    if (p.properties.code === 'feature_disabled') return <div className="qm-perf"><div className="qm-perf__notice" data-testid="ga4-disabled"><p className="qm-perf__notice-text">{t('connectors.ga4.disabled')}</p></div></div>;
    return (
      <div className="qm-perf">
        <div className="qm-perf__notice" role="alert" data-tone="error">
          <p className="qm-perf__notice-text">{t('connectors.ga4.error')}</p>
          {p.onRetry ? <Button onClick={p.onRetry}>{t('connectors.ga4.retry')}</Button> : null}
        </div>
      </div>
    );
  }
  const list = p.properties.data;
  if (!list.length) {
    return (
      <div className="qm-perf">
        <div className="qm-perf__notice" data-testid="ga4-no-property">
          {p.integrationsHref ? (
            <>
              <Heading level={2}>{t('connectors.ga4.empty.title')}</Heading>
              <p className="qm-perf__notice-text">{t('connectors.ga4.empty.body')}</p>
              <Link className="qm-perf__cta" to={p.integrationsHref}>{t('connectors.ga4.empty.cta')}</Link>
            </>
          ) : <p className="qm-perf__notice-text">{t('connectors.ga4.empty.agency')}</p>}
        </div>
      </div>
    );
  }
  const selected = pickProperty(list, p.selectedId)!;

  const controls = (
    <div className="qm-ga4__controls">
      {list.length > 1 ? (
        <label className="qm-ga4__picker" htmlFor={ids.prop}>
          <span className="qm-perf__legend">{t('connectors.ga4.property')}</span>
          <select id={ids.prop} className="qm-ga4__select" value={selected.id} onChange={(e) => p.onSelect(e.target.value)} data-testid="ga4-property-picker">
            {list.map((x) => <option key={x.id} value={x.id}>{x.displayName ?? x.propertyRef}</option>)}
          </select>
        </label>
      ) : <p className="qm-perf__fresh" data-testid="ga4-property"><bdi>{sanitizeBidi(selected.displayName ?? selected.propertyRef)}</bdi></p>}
      <fieldset className="qm-perf__range">
        <legend className="qm-perf__legend">{t('connectors.ga4.range.label')}</legend>
        {GA4_PRESETS.map((r) => (
          <button key={r} type="button" className="qm-perf__chip" aria-pressed={p.preset === r} onClick={() => p.onPreset(r)}>{t(`connectors.ga4.range.${r}`)}</button>
        ))}
      </fieldset>
    </div>
  );

  if (!p.report || p.report.status === 'loading') return <div className="qm-perf">{controls}<p className="qm-loading" role="status" aria-live="polite">{t('connectors.ga4.loading')}</p></div>;
  if (p.report.status === 'error') {
    return (
      <div className="qm-perf">
        {controls}
        <div className="qm-perf__notice" role="alert" data-tone="error">
          <p className="qm-perf__notice-text">{t('connectors.ga4.error')}</p>
          {p.onRetry ? <Button onClick={p.onRetry}>{t('connectors.ga4.retry')}</Button> : null}
        </div>
      </div>
    );
  }
  const r = p.report.data;
  const currency = r.property.currency;
  const money = (v: number | null) => (v == null || !currency ? <NotMeasured t={t} /> : f.money(v, currency));
  const rate = (v: number | null) => (v == null ? <NotMeasured t={t} /> : f.pct(v));

  const notices = ga4Notices(r).map((n) => {
    if (n.kind === 'reconnect') {
      return (
        <div key="reconnect" className="qm-perf__banner" role="status" data-testid="ga4-reconnect">
          <p className="qm-perf__notice-text">{n.asOf ? t('connectors.ga4.stale', { time: f.when(n.asOf) }) : t('connectors.ga4.staleNever')}</p>
          {p.integrationsHref ? <Link className="qm-perf__link-inline" to={p.integrationsHref}>{t('connectors.ga4.reconnect')}</Link> : null}
        </div>
      );
    }
    if (n.kind === 'paused') return <div key="paused" className="qm-perf__banner" role="status" data-testid="ga4-paused"><p className="qm-perf__notice-text">{t(`connectors.ga4.paused.${n.reason}`, { time: f.when(n.until) })}</p></div>;
    return <div key="missing" className="qm-perf__banner" role="status" data-testid="ga4-missing"><p className="qm-perf__notice-text">{t('connectors.ga4.missing', { n: f.num(n.days) })}</p></div>;
  });

  const tiles: { key: string; label: string; value: ReactNode; text: string; href: string }[] = [
    { key: 'sessions', label: t('connectors.ga4.metric.sessions'), value: f.num(r.totals.sessions), text: f.num(r.totals.sessions), href: `#${ids.daily}` },
    { key: 'users', label: t('connectors.ga4.metric.avgDailyUsers'), value: r.totals.avgDailyUsers == null ? <NotMeasured t={t} /> : f.num(r.totals.avgDailyUsers), text: r.totals.avgDailyUsers == null ? t('connectors.ga4.notMeasured') : f.num(r.totals.avgDailyUsers), href: `#${ids.daily}` },
    { key: 'newUsers', label: t('connectors.ga4.metric.newUsers'), value: f.num(r.totals.newUsers), text: f.num(r.totals.newUsers), href: `#${ids.channel}` },
    { key: 'engagementRate', label: t('connectors.ga4.metric.engagementRate'), value: rate(r.totals.engagementRate), text: r.totals.engagementRate == null ? t('connectors.ga4.notMeasured') : f.pct(r.totals.engagementRate), href: `#${ids.channel}` },
    { key: 'keyEvents', label: t('connectors.ga4.metric.keyEvents'), value: f.num(r.totals.keyEvents), text: f.num(r.totals.keyEvents), href: `#${ids.source}` },
    { key: 'revenue', label: t('connectors.ga4.metric.revenue'), value: money(r.totals.revenueMinor), text: r.totals.revenueMinor == null || !currency ? t('connectors.ga4.notMeasured') : f.money(r.totals.revenueMinor, currency), href: `#${ids.source}` },
  ];

  const metricCols = <T extends Row>(): Column<T & { id: string }>[] => [
    { key: 'sessions', header: t('connectors.ga4.metric.sessions'), numeric: true, render: (x) => f.num(x.sessions) },
    { key: 'newUsers', header: t('connectors.ga4.metric.newUsers'), numeric: true, render: (x) => f.num(x.newUsers) },
    { key: 'engaged', header: t('connectors.ga4.metric.engagedSessions'), numeric: true, render: (x) => f.num(x.engagedSessions) },
    { key: 'rate', header: t('connectors.ga4.metric.engagementRate'), numeric: true, render: (x) => rate(x.engagementRate) },
    { key: 'keyEvents', header: t('connectors.ga4.metric.keyEvents'), numeric: true, render: (x) => f.num(x.keyEvents) },
    { key: 'revenue', header: t('connectors.ga4.metric.revenue'), numeric: true, render: (x) => money(x.revenueMinor) },
  ];
  const valueLabel = (v: string) => (v === '(other)' ? t('connectors.ga4.other') : v);
  const dailyCols: Column<Ga4Report['daily'][number] & { id: string }>[] = [
    { key: 'date', header: t('connectors.ga4.col.date'), render: (x) => f.day(x.date) },
    { key: 'users', header: t('connectors.ga4.metric.users'), numeric: true, render: (x) => f.num(x.users) },
    ...metricCols<Ga4Report['daily'][number]>(),
  ];
  const dimCols = (header: string): Column<Ga4Report['byChannel'][number] & { id: string }>[] => [
    { key: 'value', header, render: (x) => <bdi>{sanitizeBidi(valueLabel(x.value))}</bdi> },
    ...metricCols<Ga4Report['byChannel'][number]>(),
  ];

  return (
    <div className="qm-perf qm-ga4" data-testid="ga4-report">
      {controls}
      <div className="qm-ga4__meta">
        <p className="qm-perf__fresh" data-testid="ga4-asof">{r.asOf ? t('connectors.ga4.asOf', { time: f.when(r.asOf) }) : t('connectors.ga4.neverSynced')}</p>
        {p.onSync && r.current ? <Button onClick={p.onSync} disabled={p.syncing} data-testid="ga4-sync">{t('connectors.ga4.syncNow')}</Button> : null}
        {p.syncResult ? <p className="qm-perf__fresh" role="status">{t(p.syncResult.status === 'succeeded' ? 'connectors.ga4.syncDone' : p.syncResult.status === 'paused' ? 'connectors.ga4.syncPaused' : 'connectors.ga4.syncFailed')}</p> : null}
      </div>
      {notices}
      {ga4Empty(r) ? (
        <div className="qm-perf__notice" data-testid="ga4-empty-range"><p className="qm-perf__notice-text">{t('connectors.ga4.noData')}</p></div>
      ) : (
        <>
          <section className="qm-perf__section" aria-labelledby={ids.tiles} data-testid="ga4-tiles">
            <Heading level={2} id={ids.tiles} className="qm-sr-only">{t('connectors.ga4.title')}</Heading>
            <dl className="qm-perf__tiles">
              {tiles.map((x) => (
                <div key={x.key} className="qm-perf__tile">
                  <dt className="qm-perf__tile-label">{x.label}</dt>
                  <dd className="qm-perf__tile-value">
                    <a className="qm-ga4__tile-link" href={x.href} aria-label={t('connectors.ga4.tile.open', { metric: x.label, value: x.text })} data-testid={`ga4-tile-${x.key}`}>{x.value}</a>
                  </dd>
                </div>
              ))}
            </dl>
            <p className="qm-perf__note">{t('connectors.ga4.usersNote')}</p>
          </section>
          <section className="qm-perf__section" aria-labelledby={ids.daily} data-testid="ga4-daily">
            <Heading level={2} id={ids.daily}>{t('connectors.ga4.section.daily')}</Heading>
            <DataTable caption={t('connectors.ga4.section.daily')} columns={dailyCols} rows={[...r.daily].reverse().map((d) => ({ ...d, id: d.date }))} />
          </section>
          <section className="qm-perf__section" aria-labelledby={ids.channel} data-testid="ga4-channel">
            <Heading level={2} id={ids.channel}>{t('connectors.ga4.section.channel')}</Heading>
            <DataTable caption={t('connectors.ga4.section.channel')} columns={dimCols(t('connectors.ga4.col.channel'))} rows={r.byChannel.map((x) => ({ ...x, id: x.value }))} />
          </section>
          <section className="qm-perf__section" aria-labelledby={ids.source} data-testid="ga4-source">
            <Heading level={2} id={ids.source}>{t('connectors.ga4.section.sourceMedium')}</Heading>
            <DataTable caption={t('connectors.ga4.section.sourceMedium')} columns={dimCols(t('connectors.ga4.col.sourceMedium'))} rows={r.bySourceMedium.map((x) => ({ ...x, id: x.value }))} />
          </section>
        </>
      )}
    </div>
  );
}

function useAsync<T>(fn: () => Promise<T>, deps: unknown[], enabled = true): [Async<T>, () => void] {
  const [state, setState] = useState<Async<T>>({ status: 'loading' });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    setState({ status: 'loading' });
    fn().then((data) => { if (live) setState({ status: 'ready', data }); }, (e: unknown) => { if (live) setState({ status: 'error', code: codeOf(e) }); });
    return () => { live = false; };
  }, [...deps, tick, enabled]);
  return [state, useCallback(() => setTick((n) => n + 1), [])];
}

export function Ga4View({ source, t, locale, integrationsHref, now = () => new Date() }: { source: Ga4Source; t: Translate; locale: Locale; integrationsHref: string | null; now?: () => Date }) {
  const [preset, setPreset] = useState<Ga4Preset>('28d');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const range = useMemo(() => ga4RangeFor(preset, now()), [preset]);
  const [properties, reloadProps] = useAsync(() => source.properties(), [source]);
  const selected = properties.status === 'ready' ? pickProperty(properties.data, selectedId) : null;
  const [report, reloadReport] = useAsync(() => source.report(selected!.id, range), [source, selected?.id, range.from, range.to], !!selected);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<Ga4SyncResult | null>(null);
  const onSync = source.sync && selected ? async () => {
    setSyncing(true); setSyncResult(null);
    try { setSyncResult(await source.sync!(selected.id)); reloadReport(); } catch { setSyncResult({ propertyId: selected.id, status: 'failed', code: null, rows: 0 }); }
    setSyncing(false);
  } : undefined;
  return (
    <Ga4Body properties={properties} report={selected ? report : null} selectedId={selected?.id ?? null} onSelect={setSelectedId} preset={preset} onPreset={setPreset}
      t={t} locale={locale} integrationsHref={integrationsHref} onRetry={() => { reloadProps(); reloadReport(); }}
      onSync={onSync ? () => void onSync() : undefined} syncing={syncing} syncResult={syncResult} />
  );
}
